import { useEffect, type ReactNode } from 'react'
import { Icon } from '@/shared/components/atoms/Icon'

interface ModalProps {
	open: boolean
	onClose: () => void
	title: string
	children: ReactNode
	maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
}

const MAX_WIDTH_CLASS: Record<NonNullable<ModalProps['maxWidth']>, string> = {
	sm: 'max-w-sm',
	md: 'max-w-md',
	lg: 'max-w-lg',
	xl: 'max-w-xl',
	'2xl': 'max-w-2xl',
	'3xl': 'max-w-3xl',
}

/**
 * Tiny modal shell used by admin edit/create dialogs. Mirrors the existing
 * `FileShareDialog` style (plain overlay div, no Radix portal) so the
 * admin pages don't introduce a new primitive in this PR. Closes on Escape
 * and on backdrop click.
 */
export function Modal({ open, onClose, title, children, maxWidth = 'lg' }: ModalProps) {
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', onKey)
		return () => window.removeEventListener('keydown', onKey)
	}, [open, onClose])

	if (!open) return null
	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
			role="dialog"
			aria-modal="true"
			aria-label={title}
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose()
			}}
		>
			<div className={`w-full ${MAX_WIDTH_CLASS[maxWidth]} rounded-lg bg-white p-6 shadow-xl`}>
				<div className="mb-4 flex items-center justify-between">
					<h3 className="text-lg font-semibold">{title}</h3>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="text-muted-foreground transition-colors hover:text-muted-foreground"
					>
						<Icon name="X" size="md" />
					</button>
				</div>
				{children}
			</div>
		</div>
	)
}
